import Papa from 'papaparse'
import { downloadFile } from 'shared/utils/fileExport'
import { retrieveContentUrl } from './components/ingestion/Utility'
import { INode } from 'shared/types/tree'
import {
  httpAndDataUrls,
  partnerKeyForTree,
  subjectForTree,
} from './components/image_verification/utility'
import { getGradeId } from './components/utility/TreeHelpers'

/**
 * Processes an array of async operations in controlled batches.
 *
 * @param things - Items to process
 * @param asyncOp - Async function to apply to each item
 * @param inParallel - Number of concurrent chunks to process
 * @returns Promise<U[]> - Flattened or nested results of all operations
 */
export function doInBatches<T, U>(
  things: T[],
  asyncOp: (thing: T) => Promise<U>,
  inParallel = 25,
): Promise<U[]> {
  const chunkSize = Math.ceil(things.length / inParallel)

  // Split into chunks
  const chunks = things.reduce<T[][]>((acc, curr, index) => {
    const chunkIndex = Math.floor(index / chunkSize)
    if (!acc[chunkIndex]) acc[chunkIndex] = []
    acc[chunkIndex].push(curr)
    return acc
  }, [])

  // Map each chunk to a reduce chain of asyncOps
  const chunkPromises = chunks.map((chunk, chunkIndex) => {
    // When flattening, collect all results in an array
    return chunk.reduce<Promise<U[]>>((accPromise, item, localIndex) => {
      // const globalIndex = chunkIndex * chunkSize + localIndex
      return accPromise.then(async (results) => [
        ...results,
        await asyncOp(item),
      ])
    }, Promise.resolve([]))
  })
  // Return results based on flatten parameter
  return Promise.all(chunkPromises).then((result) => {
    return result.flat()
  })
}

export async function processInChunks<T, U>(
  array: T[],
  chunkSize: number,
  callback: (a: T[]) => Promise<U>,
) {
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize)
    await callback(chunk)
  }
  return Promise.resolve()
}

export function formatHtml(html: string) {
  const tab = '\t'
  let result = ''
  let indent = ''

  html.split(/>\s*</).forEach(function (element) {
    if (element.match(/^\/\w/)) {
      indent = indent.substring(tab.length)
    }

    result += indent + '<' + element + '>\r\n'

    if (element.match(/^<?\w[^>]*[^/]$/) && !element.startsWith('input')) {
      indent += tab
    }
  })
  return result.substring(1, result.length - 3)
}

export function copyToClipboard(content: string) {
  navigator.clipboard.writeText(content).then(() => {})
}

export function downloadJson(fileName: string, json: object) {
  downloadFile({
    fileName,
    fileDataString: JSON.stringify(json, null, 2),
    fileDataType: 'data:text/json;charset=utf-8',
  })
}

export function downloadCsv(fileName: string, data: string[][]) {
  downloadFile({
    fileName,
    fileDataString: Papa.unparse(data),
    fileDataType: 'data:text/csv;charset=utf-8',
  })
}

export function openInNewTab({
  data,
  mimeType = 'application/json',
  filename,
}: {
  data: object | string
  mimeType?: string
  filename?: string
}) {
  const content =
    typeof data === 'string' ? data : JSON.stringify(data, null, 2)
  const blob = new Blob([content], { type: mimeType })
  // Each call to createObjectURL creates a unique URL, preventing caching issues
  const url = URL.createObjectURL(blob)

  // Open in new tab (filename parameter kept for API compatibility but not used here)
  window.open(url, '_blank')

  // Clean up after a delay to allow the tab to load
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export async function downloadUrlMapForTree(tree: INode) {
  const subject = await subjectForTree(tree)
  if (!subject) return
  const { externalUrls } = httpAndDataUrls(tree)
  return downloadImportUrlMap({
    externalUrls,
    partnerKey: partnerKeyForTree(tree),
    subject: subject.subject_name,
    gradeId: String(getGradeId(tree)),
    envOverride: 'production',
  })
}

export async function downloadImportUrlMap({
  externalUrls,
  partnerKey,
  subject,
  gradeId,
  envOverride,
}: {
  externalUrls: string[]
  partnerKey: string
  subject: string
  gradeId: string
  envOverride?: string
}) {
  const urls = await generateS3UploadCommandsForIngestion({
    externalUrls,
    partnerKey,
    subject,
    gradeId,
    envOverride,
  })
  downloadFile({
    fileName: 'urls.txt',
    fileDataString: urls.join('\n'),
  })
}

export async function generateS3UploadCommandsForIngestion({
  externalUrls,
  partnerKey,
  subject,
  gradeId: curricula,
  envOverride,
}: {
  externalUrls: string[]
  partnerKey: string
  subject: string
  gradeId: string
  envOverride?: string
}) {
  let uploadUrl = await retrieveContentUrl({
    partnerKey,
    subject,
    curricula,
  })
  if (envOverride) {
    uploadUrl = uploadUrl.replace(
      'kiddom-media-development',
      `kiddom-media-${envOverride}`,
    )
  }
  const putPath = (src: string) => {
    const fileName = src.split('/').pop()
    return `${uploadUrl}/${fileName}`
  }
  return externalUrls.reduce((acc: string[], url) => {
    acc.push(`${url},${putPath(url)}`)
    return acc
  }, [])
}

export function isObject(paramObj: unknown): boolean {
  return (
    paramObj !== null &&
    !Array.isArray(paramObj) &&
    typeof paramObj === 'object'
  )
}

export function objectTest(paramObj: unknown): paramObj is Object {
  return isObject(paramObj)
}

export function keyTest(obj: Object, key: string): key is keyof typeof obj {
  return key in obj
}

export function objectValueSort<T>(func: (item: T) => unknown) {
  return (a: T, b: T) => {
    const aVal = func(a)
    const bVal = func(b)
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal)
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return bVal - aVal
    }
    return 0
  }
}

export function objectFieldSort<TObj, TProp extends keyof TObj>(
  ...fields: TProp[]
) {
  return (a: TObj, b: TObj) => {
    for (const field of fields) {
      const av = a[field]
      const bv = b[field]

      if (av === bv) continue
      return av < bv ? -1 : 1
    }
    return 0
  }
}

export function indexFieldSort<TObj, TProp extends keyof TObj>(
  field: TProp,
  indexedList: Record<string | number, TObj>,
) {
  return (indexA: string | number, indexB: string | number) => {
    const aVal = indexedList[indexA]?.[field]
    const bVal = indexedList[indexB]?.[field]
    if (!aVal && !bVal) return 0
    if (!aVal) return 1
    if (!bVal) return -1
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal)
    }
    return 0
  }
}

export function toTitleCase(s: string) {
  return s.replace(/(^|\s)\S/g, function (t) {
    return t.toUpperCase()
  })
}

export function snakeKabobToTitleCase(snaked: string) {
  const toReplaceRegex = /[_-]/g
  return toTitleCase(snaked.replaceAll(toReplaceRegex, ' '))
}

export function alphabetIndex(char: string) {
  return char.toLowerCase().charCodeAt(0) - 'a'.charCodeAt(0)
}

type FirstAndLastType<T> = [T, T] | [T] | []
export function firstAndLast<T>([first, ...rest]: T[]): FirstAndLastType<T> {
  return [first, rest.pop()].filter((t) => !!t) as FirstAndLastType<T>
}

export function splitOnMatches(content: string, regex: RegExp) {
  const matches = content.match(regex)
  if (!matches) return []
  const indexes = matches.map((match) => content.indexOf(match))
  return indexes.reduce<string[]>((acc, currentIndex, index) => {
    // Skip the first index, we need a previous to get substring
    if (index === 0) return acc
    const previousEndIndex = indexes[index - 1]
    acc.push(content.slice(previousEndIndex, currentIndex))
    if (index === indexes.length - 1 && currentIndex < content.length) {
      acc.push(content.slice(currentIndex))
    }
    return acc
  }, [])
}

export function cloneDeep(obj: object) {
  return JSON.parse(JSON.stringify(obj))
}

export function convertStringToRegex(ugc: string, includeFlags: string[] = []) {
  if (ugc.startsWith('/')) {
    const optionsIndex = ugc.lastIndexOf('/')
    const [rPart, options] = [
      ugc.substring(1, optionsIndex),
      ugc.substring(optionsIndex + 1),
    ]
    try {
      return new RegExp(rPart, options + includeFlags.join(''))
    } catch {
      return undefined
    }
  }
}

export function makeRegex(maybeRegex: string) {
  const regexLiteral = /^\/(.+)\/([a-z]*)$/i // matches /pattern/flags

  const match = maybeRegex.match(regexLiteral)
  if (match) {
    const pattern = match[1]
    const flags = match[2]
    return new RegExp(pattern, flags)
  } else {
    // treat as a plain string, escape special chars
    const escaped = maybeRegex.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    return new RegExp(escaped)
  }
}

export function jsonFile(jsonObject: object, filename: string): File {
  const validated = JSON.stringify(jsonObject, null, 2)
  const blob = new Blob([validated], { type: 'application/json' })
  return new File([blob], filename, { type: 'application/json' })
}

export type WithRequiredProperty<Type, Key extends keyof Type> = Type & {
  [Property in Key]-?: Type[Property]
}
